import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useEntity } from '@/hooks/useEntity';
import { useProfile } from '@/hooks/useProfile';
import { ENTITY_TYPES } from '@/lib/constants/entity-types';
import { getPrimaryRole } from '@/lib/constants/roles';
import { DASHBOARD_CONFIG } from '@/lib/constants/dashboard';
import { createStartupCtaLabel } from '@/lib/constants/navigation';
import { resolvePersonaKey } from '@/lib/utils/resolve-persona-key';
import { prefetchGeographyAndTaxonomy } from '@/features/startups/hooks/prefetchGeographyAndTaxonomy';
import { useHasInProgressStartupDraft } from '@/features/startups/hooks/useHasInProgressStartupDraft';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { UnlockFeaturesBanner } from '@/features/dashboard/components/UnlockFeaturesBanner';
import { DataRoomDashboardCard } from '@/features/data-room/components/DataRoomDashboardCard';
import { DashboardFeaturedEvent } from '@/features/dashboard/components/DashboardFeaturedEvent';
import { DashboardUpcomingEvents } from '@/features/dashboard/components/DashboardUpcomingEvents';
import { DashboardLinkCard } from '@/features/dashboard/components/DashboardLinkCard';
import { DashboardProfileCompleteness } from '@/features/dashboard/components/DashboardProfileCompleteness';
import { DashboardInvestorProfileCta } from '@/features/dashboard/components/DashboardInvestorProfileCta';
import { DashboardResources } from '@/features/dashboard/components/DashboardResources';
import { DashboardRegisteredEvents } from '@/features/dashboard/components/DashboardRegisteredEvents';
import { DashboardLatestPerks } from '@/features/dashboard/components/DashboardLatestPerks';
import { DashboardTeamSnapshot } from '@/features/dashboard/components/DashboardTeamSnapshot';
import { DASHBOARD_STRINGS } from '@/lib/constants/dashboard-strings';
import { ROUTES } from '@/lib/constants/routes';

/**
 * Shared `/dashboard` route for every persona. Content is resolved from
 * DASHBOARD_CONFIG keyed on (role, tier) via resolvePersonaKey — no per-role
 * or per-tier branching in this component. All nine FR-15 personas are
 * covered as registry entries; an unknown persona falls closed to individual.
 */
export function DashboardPage() {
  const { user, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const entity = useEntity();
  const primaryRole = getPrimaryRole(user?.roles ?? []);
  const config = DASHBOARD_CONFIG[resolvePersonaKey(primaryRole, user?.tier ?? 'free')];
  // The person's name, never their role (client: greet by name, UX sheet v2).
  // Plain "Welcome" while the profile loads or when no name is saved yet.
  const firstName = useProfile().data?.firstName?.trim();
  const welcomeTitle = firstName ? DASHBOARD_STRINGS.WELCOME_NAMED(firstName) : config.welcome.title;
  // A CTA into the create-startup wizard reads the same as the navbar's:
  // "Resume Startup Profile" while this user has a draft (UX sheet r154/r183).
  const hasStartupDraft = useHasInProgressStartupDraft();
  const primaryCtaLabel =
    config.primaryCta?.route === ROUTES.CREATE_STARTUP
      ? createStartupCtaLabel(hasStartupDraft)
      : config.primaryCta?.label;

  // Which startup, if any, this session is acting for: completeness is scored
  // for startup profiles only (MYN-254), and the data-room card is a startup's.
  const startupMembership = entity?.entityType === ENTITY_TYPES.STARTUP ? entity : null;
  // The investor's complete-your-profile prompt: the persona gate says whether this
  // dashboard carries it, the membership says which investor it is for (MYN-275).
  const investorMembership =
    config.gates.showInvestorProfileCta && entity?.entityType === ENTITY_TYPES.INVESTOR
      ? entity
      : null;

  // Warm the create-startup wizard's HQ/Sector dropdowns before the
  // individual ever clicks "Create Startup" (MYN-46).
  useEffect(() => {
    if (config.gates.showCreateStartupCta) {
      prefetchGeographyAndTaxonomy(queryClient);
    }
  }, [config.gates.showCreateStartupCta, queryClient]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-8" aria-label="Loading dashboard">
        <Skeleton className="h-[6.25rem] w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    // The side column sits beside the content from a normal laptop width (xl,
    // 1280px) up; narrower screens show it above the events as a compact grid
    // (client ask, MYN-274). UX round 2 (MYN-274) reversed the earlier 2xl-only
    // rule: at a full ~1440px laptop window the column was missing from view.
    // A grid rather than two flex columns so that, stacked, the Welcome banner
    // can sit ABOVE the side column while the rest of the content stays below
    // it (UX round 3, StartupAdminPatty Q7). The `1fr` second row takes the
    // side column's extra height, so the banner row never grows a gap.
    <div className="grid grid-cols-1 gap-6 p-8 xl:grid-cols-[minmax(0,1fr)_23.4375rem] xl:grid-rows-[auto_1fr] xl:items-start">
      <div
        className="rounded-card border-navy/10 order-1 flex min-h-[6.25rem] w-full flex-col justify-center gap-1 border px-[2.0625rem] py-[1.5625rem] shadow-[0_3px_16px_0_rgba(0,25,40,0.07)] xl:col-start-1 xl:row-start-1"
        style={{ backgroundImage: 'linear-gradient(90deg, #4DBA72 0%, #0F1B3D 100%)' }}
      >
        <h1 className="text-[2rem] leading-[1.71875rem] font-semibold text-white">
          {welcomeTitle}
        </h1>
        {config.welcome.subtitle && (
          <p className="font-medium text-white/90">{config.welcome.subtitle}</p>
        )}
        {config.primaryCta && (
          <Button
            asChild
            variant="outline"
            className="mt-3 self-start border-white text-white hover:bg-white/10"
          >
            {config.primaryCta.openInNewTab ? (
              <Link to={config.primaryCta.route} target="_blank" rel="noopener noreferrer">
                {primaryCtaLabel}
              </Link>
            ) : (
              <Link to={config.primaryCta.route}>{primaryCtaLabel}</Link>
            )}
          </Button>
        )}
      </div>

      <div className="order-3 flex min-w-px flex-col gap-6 xl:col-start-1 xl:row-start-2">
        {/* Directly under the banner and above the featured event, so it is first among the
            content blocks and in view on a laptop; never in the side column (FR-185). */}
        {investorMembership && <DashboardInvestorProfileCta membership={investorMembership} />}
        {startupMembership && <DashboardProfileCompleteness membership={startupMembership} />}
        <DashboardFeaturedEvent />
        <DashboardUpcomingEvents />
        <DashboardResources />
      </div>

      {/* 25% narrower than the original 31.25rem (UX sheet, StartupAdminVF r13).
          order-2 is visual only, so screen readers still reach the main content first. */}
      <div className="order-2 grid w-full grid-cols-1 gap-6 md:grid-cols-2 xl:col-start-2 xl:row-span-2 xl:row-start-1 xl:flex xl:w-[23.4375rem] xl:flex-col">
        {config.gates.showUnlockBanner && <UnlockFeaturesBanner />}

        {/* One top-right data-room card for every startup persona (client
            copy, 2026-10-12): the Legal Command Center for an SR NEXT client,
            the Issue Report card otherwise. Access is the Data Room page's
            call, never the dashboard's. */}
        {startupMembership && <DataRoomDashboardCard membership={startupMembership} />}

        {/* Small, real-data cards only: the client calls this column the least
            important part of the page (MYN-254). They replace the placeholder
            count tiles and Recent Activity feed (UX sheet, StartupAdminVF
            r10-r12, r19); Latest Perks replaces the Perks link card with
            its own View all (MYN-252). */}
        <DashboardLinkCard
          to={ROUTES.TRUSTED_PARTNERS}
          lead={DASHBOARD_STRINGS.TRUSTED_PARTNERS.LEAD}
          title={DASHBOARD_STRINGS.TRUSTED_PARTNERS.TITLE}
        />
        <DashboardRegisteredEvents />
        <DashboardLatestPerks />
        {entity && <DashboardTeamSnapshot membership={entity} />}
      </div>
    </div>
  );
}
